import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { StaticImage } from 'gatsby-plugin-image'
import React, { useEffect, useState } from 'react'

interface CookieBotWindow extends Window {
  Cookiebot?: any
  CookieConsent?: any
}

declare const window: CookieBotWindow

const CookieBanner: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false)
  const [deviceType, setDeviceType] = useState('')
  const [showCustomiseDialog, setShowCustomiseDialog] = useState(false)
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    preferences: true,
    statistics: true,
    marketing: true
  })

  // Section visibility states
  const [visibleSections, setVisibleSections] = useState({
    necessary: false,
    preferences: false,
    statistics: false,
    marketing: false
  })

  const ariaLabelledBy = `CookieBannerTitle-${deviceType}`

  useEffect(() => {
    const determineDeviceType = () => {
      setDeviceType(window.innerWidth <= 768 ? 'Mobile' : 'Desktop')
    }

    determineDeviceType()
    window.addEventListener('resize', determineDeviceType)

    return () => window.removeEventListener('resize', determineDeviceType)
  }, [])

  useEffect(() => {
    // Hide Cookiebot straight away using CSS
    const style = document.createElement('style')
    style.innerHTML = `
      #CookieConsentDialog,
      .CookieDeclaration,
      div[id^="CybotCookiebotDialog"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
      }
    `
    document.head.appendChild(style)

    // Check if Cookiebot is loaded
    const checkCookiebot = () => {
      if (window.Cookiebot) {
        // Hide the default Cookiebot dialog
        if (typeof window.Cookiebot.hide === 'function') {
          window.Cookiebot.hide()
        }

        // Alternative way to hide Cookiebot
        if (window.Cookiebot.dialog && window.Cookiebot.dialog.style) {
          window.Cookiebot.dialog.style.display = 'none'
        }

        // Also try to hide via the widget property
        if (window.Cookiebot.widget) {
          window.Cookiebot.widget.display = false
        }

        // Show our custom banner if consent hasn't been given
        if (!window.Cookiebot.hasResponse) {
          setShowBanner(true)
        }
        return true
      }
      return false
    }

    // If Cookiebot isn't loaded yet, set up a listener for when it loads
    if (!checkCookiebot()) {
      window.addEventListener('CookiebotOnLoad', () => {
        // Hide the default Cookiebot dialog
        if (window.Cookiebot) {
          // Try multiple methods to ensure Cookiebot is hidden
          if (typeof window.Cookiebot.hide === 'function') {
            window.Cookiebot.hide()
          }

          // Show our custom banner if consent hasn't been given
          if (!window.Cookiebot.hasResponse) {
            setShowBanner(true)
          }
        }
      })
    }

    // Set up event listeners for Cookiebot events
    window.addEventListener('CookiebotOnAccept', () => {
      setShowBanner(false)
    })

    // Hide Cookiebot when it tries to display
    window.addEventListener('CookiebotOnDialogDisplay', () => {
      if (window.Cookiebot) {
        // Try multiple methods to ensure Cookiebot is hidden
        if (typeof window.Cookiebot.hide === 'function') {
          window.Cookiebot.hide()
        }

        // Show our custom banner instead
        if (!window.Cookiebot.hasResponse) {
          setShowBanner(true)
        }
      }
    })

    // Add a MutationObserver to detect and hide the Cookiebot dialog if it appears in the DOM
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.addedNodes.length) {
          for (let i = 0; i < mutation.addedNodes.length; i++) {
            const node = mutation.addedNodes[i] as HTMLElement
            if (node.id === 'CookieConsentDialog' || (node.classList && node.classList.contains('CookieDeclaration'))) {
              node.style.display = 'none'
            }
          }
        }
      })
    })

    // Start observing the document body for changes
    observer.observe(document.body, { childList: true, subtree: true })

    // Clean up event listeners and observer on component unmount
    return () => {
      window.removeEventListener('CookiebotOnLoad', () => {})
      window.removeEventListener('CookiebotOnAccept', () => {})
      window.removeEventListener('CookiebotOnDialogDisplay', () => {})
      document.head.removeChild(style)
      observer.disconnect()
    }
  }, [])

  const handleAcceptAll = () => {
    if (window.Cookiebot) {
      // window.Cookiebot.submitConsent()
      window.Cookiebot.submitCustomConsent(true, true, true)
      setShowBanner(false)
    }
  }

  const handleCustomise = () => {
    setShowCustomiseDialog(true)
  }

  const handleAcceptSelected = () => {
    if (window.Cookiebot) {
      window.Cookiebot.submitCustomConsent(
        cookiePreferences.preferences,
        cookiePreferences.statistics,
        cookiePreferences.marketing
      )
      setShowBanner(false)
    } else if (window.CookieConsent) {
      window.CookieConsent.submitCustomConsent(
        cookiePreferences.preferences,
        cookiePreferences.statistics,
        cookiePreferences.marketing
      )
      setShowBanner(false)
    }
  }

  const toggleSection = (section: keyof typeof visibleSections) => {
    setVisibleSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const handleCheckboxChange = (type: keyof typeof cookiePreferences) => {
    if (type === 'necessary') return // Necessary cookies can't be toggled

    setCookiePreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }))
  }

  if (!showBanner) {
    return null
  }

  const cookieConsentBanner = () => {
    return (
      <div className="relative flex items-center justify-center px-4 py-2 mx-auto">
        <StaticImage
          objectFit="contain"
          className="h-4 lg:h-5 w-auto mr-1"
          src="../../../images/cookie.svg"
          alt="Cookie"
        />

        <div
          id={ariaLabelledBy}
          className="font-sans font-semibold text-base lg:text-lg items-center hidden lg:flex"
          role="status"
        >
          Cookie consent
        </div>
        <div
          id={ariaLabelledBy}
          className="font-sans font-semibold text-base lg:text-lg flex items-center lg:hidden"
          role="status"
        >
          We use cookies
        </div>
      </div>
    )
  }

  return (
    <div id="cookieBannerRoot">
      <div
        id="cookieContainer"
        className="fixed inset-0 z-[2147483645] bg-black bg-opacity-70 flex justify-center items-end pointer-events-none lg:items-center mx-auto pb-10 lg:pb-0"
      >
        <div id="CookieBannerOverlay" className="pointer-events-none"></div>
        <div
          id="CookieBannerNotice"
          className="md:p-12 lg:p-16 flex justify-center items-center mx-auto w-11/12 md:w-auto pointer-events-auto"
          role="dialog"
          aria-labelledby={ariaLabelledBy}
          aria-describedby="CookieBannerDescription"
        >
          {/* Initial Dialog */}
          {!showCustomiseDialog ? (
            <div className="bg-white p-5 text-center rounded-3xl" id="initialDialog">
              {cookieConsentBanner()}

              <div
                id="CookieBannerDescription"
                className="font-sans font-medium text-sm lg:text-base flex justify-center items-center mx-auto w-full lg:w-5/6 lg:mb-4"
              >
                We use cookies to enhance your browsing experience and analyse our traffic.
                {/* Please choose which cookies you would like to accept. */}
              </div>

              <div className="flex justify-center flex-col lg:flex-row items-center" id="cookieButtons">
                <button
                  className="text-primary underline underline-offset-4 font-sans text-base flex justify-center items-center mx-auto w-full md:w-1/2 rounded-full px-6 py-3 font-semibold"
                  id="customiseCookies"
                  onClick={handleCustomise}
                >
                  Customise
                </button>

                <button
                  className="bg-primary text-white font-sans text-base flex justify-center items-center mx-auto w-full md:w-1/2 rounded-full px-6 py-3 font-semibold"
                  id="acceptCookies"
                  onClick={handleAcceptAll}
                >
                  Accept Cookies
                </button>
              </div>
            </div>
          ) : (
            /* Customise Dialog */
            <div className="bg-white p-5 text-center rounded-3xl" id="customiseDialog">
              {cookieConsentBanner()}
              <div
                id="CookieBannerDescriptionCustomise"
                className="font-sans font-medium lg:text-base text-sm flex justify-center items-center text-center mx-auto w-full mb-4 lg:justify-start lg:items-start lg:text-left"
              >
                Customise your cookie preferences. You can enable or disable different types of cookies below.
              </div>

              {/* Cookie option sections */}
              <div className="flex flex-col items-center border-t border-b border-[#c4c4c4] w-full pt-4 pb-4 px-2">
                <div className="flex w-full justify-between">
                  <div className="inline-flex items-center">
                    <div className="font-sans text-lg font-semibold text-base">Necessary</div>
                    <div
                      className={`ml-2 ${visibleSections.necessary ? 'hidden' : ''}`}
                      onClick={() => toggleSection('necessary')}
                    >
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className="h-6 w-6 pt-1 font-bold text-primary"
                        aria-hidden="true"
                      />
                    </div>
                    <div
                      className={`ml-2 ${!visibleSections.necessary ? 'hidden' : ''}`}
                      onClick={() => toggleSection('necessary')}
                    >
                      <FontAwesomeIcon
                        icon={faChevronUp}
                        className="h-6 w-6 pt-1 font-bold text-primary"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                  <div className="relative inline-block w-[60px] h-[30px] opacity-30 cursor-not-allowed">
                    <input id="necessaryCheckbox" type="checkbox" checked disabled className="sr-only peer" />
                    <div className="absolute top-0 left-0 right-0 bottom-0 bg-[#5f6977] rounded-full transition peer-checked:bg-[#006685]"></div>
                    <div className="absolute left-[2px] bottom-[2px] h-[26px] w-[26px] bg-white rounded-full transition-transform peer-checked:translate-x-[30px]"></div>
                  </div>
                </div>
                <div className={`py-2 ${!visibleSections.necessary ? 'hidden' : ''}`}>
                  <p className="font-sans text-base font-medium text-left -mb-4">
                    Necessary cookies help make a website usable by enabling basic functions like page navigation and
                    access to secure areas of the website. The website cannot function properly without these cookies.
                  </p>
                </div>
              </div>

              <div className="flex flex-col border-b border-[#c4c4c4] w-full pt-4 pb-4 px-2">
                <div className="flex w-full justify-between">
                  <div className="inline-flex items-center">
                    <div className="font-sans text-lg font-semibold text-base">Preferences</div>
                    <div
                      className={`ml-2 ${visibleSections.preferences ? 'hidden' : ''}`}
                      onClick={() => toggleSection('preferences')}
                    >
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className="h-6 w-6 pt-1 font-bold text-primary"
                        aria-hidden="true"
                      />
                    </div>
                    <div
                      className={`ml-2 ${!visibleSections.preferences ? 'hidden' : ''}`}
                      onClick={() => toggleSection('preferences')}
                    >
                      <FontAwesomeIcon
                        icon={faChevronUp}
                        className="h-6 w-6 pt-1 font-bold text-primary"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="relative inline-block w-[60px] h-[30px]">
                      <input
                        id="preferenceCheckbox"
                        type="checkbox"
                        className="opacity-0 w-0 h-0 peer"
                        checked={cookiePreferences.preferences}
                        onChange={() => handleCheckboxChange('preferences')}
                      />
                      <span
                        className={`absolute top-0 left-0 right-0 bottom-0 bg-[#5f6977] rounded-full transition duration-400 peer-checked:bg-[#006685] peer-focus:ring-1 peer-focus:ring-[#006685] before:content-[''] before:absolute before:h-[26px] before:w-[26px] before:left-[2px] before:bottom-[2px] before:bg-white before:rounded-full before:transition-transform before:duration-400 peer-checked:before:translate-x-[30px]`}
                      />
                    </label>
                  </div>
                </div>
                <div className={`py-2 ${!visibleSections.preferences ? 'hidden' : ''}`}>
                  <p className="font-sans text-base font-medium text-left -mb-4">
                    Preference cookies enable a website to remember information that changes the way the website behaves
                    or looks, like your preferred language or the region that you are in.
                  </p>
                </div>
              </div>

              <div className="flex flex-col border-b border-[#c4c4c4] w-full pt-4 pb-4 px-2">
                <div className="flex w-full justify-between">
                  <div className="inline-flex items-center">
                    <div className="font-sans text-lg font-semibold text-base">Statistics</div>
                    <div
                      className={`ml-2 ${visibleSections.statistics ? 'hidden' : ''}`}
                      onClick={() => toggleSection('statistics')}
                    >
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className="h-6 w-6 pt-1 font-bold text-primary"
                        aria-hidden="true"
                      />
                    </div>
                    <div
                      className={`ml-2 ${!visibleSections.statistics ? 'hidden' : ''}`}
                      onClick={() => toggleSection('statistics')}
                    >
                      <FontAwesomeIcon
                        icon={faChevronUp}
                        className="h-6 w-6 pt-1 font-bold text-primary"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="relative inline-block w-[60px] h-[30px]">
                      <input
                        id="statisticsCheckbox"
                        type="checkbox"
                        className="opacity-0 w-0 h-0 peer"
                        checked={cookiePreferences.statistics}
                        onChange={() => handleCheckboxChange('statistics')}
                      />
                      <span
                        className={`absolute top-0 left-0 right-0 bottom-0 bg-[#5f6977] rounded-full transition duration-400 peer-checked:bg-[#006685] peer-focus:ring-1 peer-focus:ring-[#006685] before:content-[''] before:absolute before:h-[26px] before:w-[26px] before:left-[2px] before:bottom-[2px] before:bg-white before:rounded-full before:transition-transform before:duration-400 peer-checked:before:translate-x-[30px]`}
                      />
                    </label>
                  </div>
                </div>
                <div className={`py-2 ${!visibleSections.statistics ? 'hidden' : ''}`}>
                  <p className="font-sans text-base font-medium text-left -mb-4">
                    Statistic cookies help website owners to understand how visitors interact with websites by
                    collecting and reporting information anonymously.
                  </p>
                </div>
              </div>

              <div className="flex flex-col border-b border-[#c4c4c4] w-full pt-4 pb-4 px-2">
                <div className="flex w-full justify-between">
                  <div className="inline-flex items-center">
                    <div className="font-sans text-lg font-semibold text-base">Marketing</div>
                    <div
                      className={`ml-2 ${visibleSections.marketing ? 'hidden' : ''}`}
                      onClick={() => toggleSection('marketing')}
                    >
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className="h-6 w-6 pt-1 font-bold text-primary"
                        aria-hidden="true"
                      />
                    </div>
                    <div
                      className={`ml-2 ${!visibleSections.marketing ? 'hidden' : ''}`}
                      onClick={() => toggleSection('marketing')}
                    >
                      <FontAwesomeIcon
                        icon={faChevronUp}
                        className="h-6 w-6 pt-1 font-bold text-primary"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="relative inline-block w-[60px] h-[30px]">
                      <input
                        id="marketingCheckbox"
                        type="checkbox"
                        className="opacity-0 w-0 h-0 peer"
                        checked={cookiePreferences.marketing}
                        onChange={() => handleCheckboxChange('marketing')}
                      />
                      <span
                        className={`absolute top-0 left-0 right-0 bottom-0 bg-[#5f6977] rounded-full transition duration-400 peer-checked:bg-[#006685] peer-focus:ring-1 peer-focus:ring-[#006685] before:content-[''] before:absolute before:h-[26px] before:w-[26px] before:left-[2px] before:bottom-[2px] before:bg-white before:rounded-full before:transition-transform before:duration-400 peer-checked:before:translate-x-[30px]`}
                      />
                    </label>
                  </div>
                </div>
                <div className={`py-2 ${!visibleSections.marketing ? 'hidden' : ''}`}>
                  <p className="font-sans text-base font-medium text-left -mb-4">
                    Marketing cookies are used to track visitors across websites. The intention is to display ads that
                    are relevant and engaging for the individual user and thereby more valuable for publishers and third
                    party advertisers.
                  </p>
                </div>
              </div>

              <div className="center my-4 py-2">
                <p className="center font-sans font-medium text-sm lg:text-base">
                  Visit our{' '}
                  <a href="https://www.differentdog.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                    &nbsp;<span className="underline">Cookies</span>&nbsp;
                  </a>{' '}
                  page for more details
                </p>
              </div>

              <div className="flex justify-center flex-col lg:flex-row items-baseline">
                <button
                  className="bg-white border-2 border-primary text-primary font-sans text-base flex justify-center items-center mx-auto w-full lg:w-auto rounded-full px-6 py-3 font-semibold mb-2 lg:mb-0 lg:mr-3"
                  onClick={handleAcceptAll}
                >
                  Accept All
                </button>
                <button
                  className="bg-primary text-white font-sans text-base flex justify-center items-center mx-auto w-full lg:w-auto rounded-full px-6 py-3 font-semibold lg:ml-3"
                  onClick={handleAcceptSelected}
                >
                  Accept Selected
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CookieBanner
